import { useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import type { AccountSummary } from '@/types/overview';
import { formatRelativeDay } from '@/lib/overview';

/**
 * Compact list of the most recently active accounts (by last in-window feedback
 * day). Click-to-select drills into that account's dashboard.
 */
export function RecentlyActiveAccounts({
  accounts,
  onSelectAccount,
  limit = 6,
}: {
  accounts: AccountSummary[];
  onSelectAccount: (accountToken: string) => void;
  limit?: number;
}) {
  const rows = useMemo(
    () =>
      accounts
        .filter((a) => a.lastFeedbackAt !== null)
        .sort((a, b) => (a.lastFeedbackAt! < b.lastFeedbackAt! ? 1 : -1))
        .slice(0, limit),
    [accounts, limit]
  );

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium">Recently active</CardTitle>
      </CardHeader>
      <CardContent>
        {rows.length === 0 ? (
          <p className="py-4 text-center text-sm text-muted-foreground">No recent activity</p>
        ) : (
          <div className="divide-y">
            {rows.map((a) => (
              <button
                key={a.accountToken}
                type="button"
                onClick={() => onSelectAccount(a.accountToken)}
                title={`Open ${a.name}`}
                className="flex w-full items-center justify-between gap-3 py-2.5 text-left hover:bg-muted/50 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <div className="min-w-0">
                  <div className="truncate text-sm font-medium">{a.name}</div>
                  <div className="truncate text-xs text-muted-foreground">{a.productName}</div>
                </div>
                <span className="shrink-0 text-xs text-muted-foreground">{formatRelativeDay(a.lastFeedbackAt)}</span>
              </button>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
